import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "./CreateServiceMain.css";
import strings from "../../../../utils/strings";
import Widgets from "../../../../widgets";
import { uploadMedia } from "../../../../modules/patientProfile/patientProfileThunk";
import Icons from "../../../../Assets/Icons";
import {
  fetchServices,
  fetchSubServices,
  createServiceItem,
  createService,
  createSubService,
} from "../../../../modules/NursignCare/ManageServices/ManageServiceThunk";
import {
  resetCreateServiceItem,
  resetCreateService,
  resetCreateSubService,
  clearSubServices,
} from "../../../../modules/NursignCare/ManageServices/ManageServiceSlice";
import {
  getLocalizedTitle,
  SERVICE_GROUP_VALIDATION_MESSAGES,
  translateManageServiceToast,
  uniqueByLocalizedTitle,
  validateServiceGroupName,
} from "../../../../utils/manageServiceValidation";

const CreateServiceMain = ({ setSelectedView }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Language state - sync with global language
  const [activeLang, setActiveLang] = useState(
    i18n.language === "vietnam" ? "vi" : "en"
  );

  // Sync with global language
  useEffect(() => {
    const lang = i18n.language === "vietnam" ? "vi" : "en";
    setActiveLang(lang);
  }, [i18n.language]);

  // Form state
  const [formData, setFormData] = useState({
    title: {
      en: "",
      vi: "",
    },
    service: "",
    subService: "",
    code: "",
    price: {
      type: "PER_TIME",
      wh: {
        opdInternationalPrice: "",
        opdLocalPrice: "",
        opdSubsidized: "",
      },
      nwh: {
        opdInternationalPrice: "",
        opdLocalPrice: "",
        opdSubsidized: "",
      },
    },
    checklist: "",
  });

  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [isServiceGroupModalOpen, setIsServiceGroupModalOpen] = useState(false);
  const [isSubServiceGroupModalOpen, setIsSubServiceGroupModalOpen] = useState(false);
  const [newServiceTitle, setNewServiceTitle] = useState({
    en: "",
    vi: "",
  });
  const [newSubServiceTitle, setNewSubServiceTitle] = useState({
    en: "",
    vi: "",
  });

  // Redux state
  const {
    services,
    subServices,
    createServiceItemSuccess,
    createServiceItemErr,
    createServiceSuccess,
    createServiceErr,
    createSubServiceSuccess,
    createSubServiceErr,
  } = useSelector((state) => state.manageService);

  // Language dropdown options
  const languageOptions = [
    { value: "en", label: "English" },
    { value: "vi", label: "Vietnamese" },
  ];

  // Service Type options
  const serviceTypeOptions = [
    { value: "PER_TIME", label: t("perTime") },
    { value: "PER_HOUR", label: t("perHour") },
  ];

  // Fetch services on mount
  useEffect(() => {
    dispatch(fetchServices({ page: 1, limit: 100 }));
  }, [dispatch]);

  // Fetch sub-services when service changes
  useEffect(() => {
    if (formData.service) {
      dispatch(fetchSubServices(formData.service));
    } else {
      dispatch(clearSubServices());
      setFormData((prev) => ({ ...prev, subService: "" }));
    }
  }, [formData.service, dispatch]);

  // Handle create service success
  useEffect(() => {
    if (createServiceErr) {
      Widgets.ToastPopup.error(translateManageServiceToast(createServiceErr, t));
      dispatch(resetCreateService());
    }
    if (createServiceSuccess && createServiceSuccess.status === "success") {
      Widgets.ToastPopup.success(t("serviceCreated") || "Service created successfully");
      dispatch(fetchServices({ page: 1, limit: 100 })); // Refresh services list
      // Auto-select the newly created service
      const newService = createServiceSuccess?.data?.service;
      if (newService) {
        setFormData((prev) => ({ ...prev, service: newService._id }));
      }
      dispatch(resetCreateService());
    }
  }, [createServiceSuccess, createServiceErr, dispatch, t]);

  // Handle create sub-service success
  useEffect(() => {
    if (createSubServiceErr) {
      Widgets.ToastPopup.error(translateManageServiceToast(createSubServiceErr, t));
      dispatch(resetCreateSubService());
    }
    if (createSubServiceSuccess && createSubServiceSuccess.status === "success") {
      Widgets.ToastPopup.success(t("subServiceCreated") || "Sub-service created successfully");
      // Refresh sub-services list
      if (formData.service) {
        dispatch(fetchSubServices(formData.service));
      }
      // Auto-select the newly created sub-service
      const newSubService = createSubServiceSuccess?.data?.subService;
      if (newSubService) {
        setFormData((prev) => ({ ...prev, subService: newSubService._id }));
      }
      dispatch(resetCreateSubService());
    }
  }, [createSubServiceSuccess, createSubServiceErr, dispatch, t, formData.service]);

  // Handle create service item success
  useEffect(() => {
    if (createServiceItemErr) {
      Widgets.ToastPopup.error(translateManageServiceToast(createServiceItemErr, t));
      dispatch(resetCreateServiceItem());
    }
    if (createServiceItemSuccess && createServiceItemSuccess.status === "success") {
      Widgets.ToastPopup.success(t("serviceItemCreated") || "Service item created successfully");
      // Reset form
      setFormData({
        title: { en: "", vi: "" },
        service: "",
        subService: "",
        code: "",
        price: {
          type: "PER_TIME",
          wh: {
            opdInternationalPrice: "",
            opdLocalPrice: "",
            opdSubsidized: "",
          },
          nwh: {
            opdInternationalPrice: "",
            opdLocalPrice: "",
            opdSubsidized: "",
          },
        },
        checklist: "",
      });
      setPdfFile(null);
      setPdfUrl("");
      setUploading(false);
      // Navigate back if callback provided
      if (setSelectedView) {
        setSelectedView("serviceList");
      } else {
        navigate(strings.navigation.nursingCareManageService);
      }
      dispatch(resetCreateServiceItem());
    }
  }, [createServiceItemSuccess, createServiceItemErr, dispatch, t, setSelectedView, navigate]);

  // Helper function to get locale string
  const getLocale = (title) => {
    if (!title) return "";
    if (typeof title === "string") return title;
    return title[activeLang] || title["en"] || title["vi"] || "";
  };

  // Handle title change
  const handleTitleChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      title: {
        ...prev.title,
        [activeLang]: value,
      },
    }));
  };

  // Handle service type change
  const handleServiceTypeChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      price: {
        ...prev.price,
        type: option.value,
      },
    }));
  };

  // Handle service change
  const handleServiceChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      service: option.value,
      subService: "", // Reset subService when service changes
    }));
  };

  // Handle sub-service change
  const handleSubServiceChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      subService: option.value,
    }));
  };

  // Handle code change
  const handleCodeChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      code: e.target.value,
    }));
  };

  // Handle price change
  const handlePriceChange = (field, value) => {
    setFormData((prev) => {
      const newPrice = JSON.parse(JSON.stringify(prev.price)); // Deep copy
      if (field.startsWith("wh.")) {
        const key = field.replace("wh.", "");
        newPrice.wh[key] = value;
      } else if (field.startsWith("nwh.")) {
        const key = field.replace("nwh.", "");
        newPrice.nwh[key] = value;
      }
      return {
        ...prev,
        price: newPrice,
      };
    });
  };

  // Handle "Same as working hours" click
  const handleSameAsWorkingHours = () => {
    setFormData((prev) => ({
      ...prev,
      price: {
        ...prev.price,
        nwh: { ...prev.price.wh },
      },
    }));
  };

  // Handle create service group
  const handleCreateServiceGroup = () => {
    // UAT validation: trim before checks and stop the API call on invalid input.
    if (
      String(newServiceTitle.en ?? "").trim().length > 100 ||
      String(newServiceTitle.vi ?? "").trim().length > 100
    ) {
      Widgets.ToastPopup.error(
        translateManageServiceToast(SERVICE_GROUP_VALIDATION_MESSAGES.maxLength, t)
      );
      return;
    }

    const englishError = validateServiceGroupName(newServiceTitle.en, services, "en");
    const vietnameseError = validateServiceGroupName(newServiceTitle.vi, services, "vi", {
      allowUnicodeLetters: true,
    });
    const validationError = englishError || vietnameseError;

    if (validationError) {
      Widgets.ToastPopup.error(translateManageServiceToast(validationError, t));
      return;
    }

    const payload = {
      title: {
        en: newServiceTitle.en.trim(),
        vi: newServiceTitle.vi.trim(),
      },
      isActive: true,
    };

    dispatch(createService(payload));
    setIsServiceGroupModalOpen(false);
    setNewServiceTitle({ en: "", vi: "" });
  };

  // Handle create sub-service group
  const handleCreateSubServiceGroup = () => {
    if (!newSubServiceTitle.en || !newSubServiceTitle.en.trim()) {
      Widgets.ToastPopup.error(t("enterSubServiceNameEnRequired"));
      return;
    }
    if (!newSubServiceTitle.vi || !newSubServiceTitle.vi.trim()) {
      Widgets.ToastPopup.error(t("enterSubServiceNameViRequired"));
      return;
    }

    if (!formData.service) {
      Widgets.ToastPopup.error(t("selectServiceGroupFirst") || "Please select a service group first");
      setIsSubServiceGroupModalOpen(false);
      setNewSubServiceTitle({ en: "", vi: "" });
      return;
    }

    const payload = {
      title: {
        en: newSubServiceTitle.en.trim(),
        vi: newSubServiceTitle.vi.trim(),
      },
      service: formData.service,
      isActive: true,
    };

    dispatch(createSubService(payload));
    setIsSubServiceGroupModalOpen(false);
    setNewSubServiceTitle({ en: "", vi: "" });
  };

  // Handle PDF file change + upload via existing upload thunk
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      Widgets.ToastPopup.error(t("onlyPdfAllowed"));
      e.target.value = "";
      return;
    }

    setPdfFile(file);
    setUploading(true);

    try {
      const res = await dispatch(uploadMedia({ files: [file] }));

      const url =
        res?.payload?.data?.url ||
        res?.payload?.url ||
        res?.payload?.data?.urls?.[0] ||
        "";

      if (!url) {
        Widgets.ToastPopup.error(t("pdfUploadFailed"));
        return;
      }

      setPdfUrl(url);
      Widgets.ToastPopup.success(t("pdfUploaded"));
    } catch (err) {
      Widgets.ToastPopup.error(t("uploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  // Remove PDF
  const handleRemovePdf = () => {
    setPdfFile(null);
    setPdfUrl("");
  };

  // Validation
  const validate = () => {
    
    if (!formData.service) {
      Widgets.ToastPopup.error(t("selectServiceGroup") || "Select Service Group");
      return false;
    }
    if (!formData.subService) {
      Widgets.ToastPopup.error(t("selectSubService") || "Select Sub Service");
      return false;
    }
    if (!formData.title.en || !formData.title.en.trim()) {
      Widgets.ToastPopup.error(t("enterEnglishTitle") || "Enter English title");
      return false;
    }
    if (!formData.title.vi || !formData.title.vi.trim()) {
      Widgets.ToastPopup.error(t("enterVietnameseTitle") || "Enter Vietnamese title");
      return false;
    }
    if (!formData.code || !formData.code.trim()) {
      Widgets.ToastPopup.error(t("enterServiceCode") || "Enter Service Code");
      return false;
    }
    return true;
  };

  const validatePriceFields = () => {
    const values = [
      formData.price.wh.opdInternationalPrice,
      formData.price.wh.opdLocalPrice,
      formData.price.wh.opdSubsidized,
      formData.price.nwh.opdInternationalPrice,
      formData.price.nwh.opdLocalPrice,
      formData.price.nwh.opdSubsidized,
    ];

    const isValid = values.every((val) => {
      const str = String(val || "").trim();
      return str !== "" && /^[0-9]+$/.test(str);
    });

    if (!isValid) {
      Widgets.ToastPopup.error(t("priceValidation"));
      return false;
    }
    return true;
  };

  // Handle create entry
  const handleCreate = () => {
    if (!validate()) return;
    if (!validatePriceFields()) return;

    // Checklist PDF required before API call
    if (!pdfUrl) {
      Widgets.ToastPopup.error(t("checklistRequired"));
      return;
    }

    // Prepare payload
    const payload = {
      title: {
        en: formData.title.en.trim(),
        vi: formData.title.vi.trim(),
      },
      service: formData.service,
      subService: formData.subService,
      code: formData.code.trim(),
      price: {
        type: formData.price.type,
        wh: {
          opdInternationalPrice: formData.price.wh.opdInternationalPrice || "0",
          opdLocalPrice: formData.price.wh.opdLocalPrice || "0",
          opdSubsidized: formData.price.wh.opdSubsidized || "0",
        },
        nwh: {
          opdInternationalPrice: formData.price.nwh.opdInternationalPrice || "0",
          opdLocalPrice: formData.price.nwh.opdLocalPrice || "0",
          opdSubsidized: formData.price.nwh.opdSubsidized || "0",
        },
      },
      checklist: pdfUrl,
      isActive: true,
    };

    dispatch(createServiceItem(payload));
  };

  // Handle cancel
  const handleCancel = () => {
    if (setSelectedView) {
      setSelectedView("serviceList");
    } else {
      navigate(strings.navigation.nursingCareManageService);
    }
  };

  // Dedupe dropdown rows by visible localized title while keeping stable IDs as values.
  const serviceOptions = useMemo(
    () =>
      uniqueByLocalizedTitle(services, activeLang).map((s) => ({
        value: s._id,
        label: getLocalizedTitle(s.title, activeLang),
      })).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" })),
    [services, activeLang]
  );

  // Dedupe API/state duplicates before rendering the sub-service dropdown.
  const subServiceOptions = useMemo(
    () =>
      uniqueByLocalizedTitle(subServices, activeLang).map((s) => ({
        value: s._id,
        label: getLocalizedTitle(s.title, activeLang),
      })).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" })),
    [subServices, activeLang]
  );

  // Get selected service type option
  const selectedServiceType = serviceTypeOptions.find(
    (opt) => opt.value === formData.price.type
  );

  // Get selected service option
  const selectedService =
    serviceOptions.find((opt) => opt.value === formData.service) ||
    (() => {
      const service = services.find((s) => s._id === formData.service);
      return service
        ? { value: service._id, label: getLocalizedTitle(service.title, activeLang) }
        : null;
    })();

  // Get selected sub-service option
  const selectedSubService =
    subServiceOptions.find((opt) => opt.value === formData.subService) ||
    (() => {
      const subService = subServices.find((s) => s._id === formData.subService);
      return subService
        ? { value: subService._id, label: getLocalizedTitle(subService.title, activeLang) }
        : null;
    })();

  // Get selected language option
  const selectedLang = languageOptions.find((opt) => opt.value === activeLang);

  const breadcrumbs = ["nursingCare", "manageService", "createService"];
  const paths = [
    strings.navigation.nursingCareManageService,
    strings.navigation.nursingCareManageService,
    strings.navigation.createService,
  ];

  return (
    <div className="mr-container">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
      <div className="manage-service-heading">{t("createService")}</div>

      <div className="create-service-main">
        {/* Language Dropdown - Top Right */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "2rem" }}>
          <Widgets.NewSelectDropdown
            options={languageOptions}
            big
            placeholder={t("selectLanguage") || "Select Language"}
            onChange={(option) => setActiveLang(option.value)}
            selectedValue={selectedLang}
          />
        </div>

        {/* Service Details Form Section */}
        <div className="service-type-main">
          {/* Service Type */}
          <div className="service-input-box">
            <span>
              {t("serviceType")} <label>*</label>
            </span>
            <Widgets.NewSelectDropdown
              options={serviceTypeOptions}
              selectedValue={selectedServiceType}
              onChange={handleServiceTypeChange}
              placeholder={t("selectServiceType") || "Select Service Type"}
              big
            />
          </div>

          <div className="group-input-box">
            {/* Service Group */}
            <div className="service-input-box">
              <span>
                {t("serviceGroup")}{activeLang === "en" ? <>(En)</> : <>(Vi)</>} <label>*</label>
              </span>
              <Widgets.NewSelectDropdown
                options={serviceOptions}
                selectedValue={selectedService}
                onChange={handleServiceChange}
                placeholder={`${t("selectServiceGroup") || "Select Service Group"} ${activeLang === "en" ? "(En)" : "(Vi)"}`}
                big
              />
              <p onClick={() => setIsServiceGroupModalOpen(true)}>
                {t("service-group") || "Create a Service Group"}
              </p>
            </div>

            {/* Sub-Service Group */}
            <div className="service-input-box">
              <span>
                {t("subServiceGroup")}{activeLang === "en" ? <>(En)</> : <>(Vi)</>} <label>*</label>
              </span>
              <Widgets.NewSelectDropdown
                options={subServiceOptions}
                selectedValue={selectedSubService}
                onChange={handleSubServiceChange}
                placeholder={`${t("selectSubServiceGroup") || "Select Sub-Service Group"} ${activeLang === "en" ? "(En)" : "(Vi)"}`}
                big
                disabled={!formData.service}
              />
              <p onClick={() => setIsSubServiceGroupModalOpen(true)}>
                {t("sub-serviceGroup") || "Create a Sub-service group"}
              </p>
            </div>

            {/* Service Item Title */}
            <div className="service-input-box">
              <span>
                {t("serviceItem")} {activeLang === "en" ? <>(En)</> : <>(Vi)</>} <label>*</label>
              </span>
              <input
                type="text"
                value={formData.title[activeLang]}
                onChange={handleTitleChange}
                placeholder={`${t("enterServiceItemTitle") || "Enter Service Item Title"} ${activeLang === "en" ? "(En)" : "(Vi)"}`}
                className="form-section-input-2-service-item"
                maxLength={100}
              />
            </div>
          </div>

          {/* Service Code */}
          <div className="service-input-box">
            <span>
              {t("serviceCode")} <label>*</label>
            </span>
            <input
              type="text"
              value={formData.code}
              onChange={handleCodeChange}
              placeholder={t("enterServiceCode") || "Enter service code"}
              className="form-section-input-2"
            />
          </div>
        </div>

        {/* Price Working Hour Section */}
        <div className="whp-container">
          <div className="title-hr">
            <p>{t("priceWorkingHour") || "Set price for working hours"}</p>
            <hr />
          </div>
          <div className="service-type-main2">
            <div className="service-input-box">
              <span>
                {t("opdInterInsured")} <label>*</label>
              </span>
              <input
                type="text"
                value={formData.price.wh.opdInternationalPrice}
                onChange={(e) =>
                  handlePriceChange("wh.opdInternationalPrice", e.target.value)
                }
                placeholder="Vnd 0"
                className="form-section-input-2"
              />
            </div>
            <div className="service-input-box">
              <span>
                {t("opdLocalInsured")} <label>*</label>
              </span>
              <input
                type="text"
                value={formData.price.wh.opdLocalPrice}
                onChange={(e) =>
                  handlePriceChange("wh.opdLocalPrice", e.target.value)
                }
                placeholder="Vnd 0"
                className="form-section-input-2"
              />
            </div>
            <div className="service-input-box">
              <span>
                {t("opdSubsidized")} <label>*</label>
              </span>
              <input
                type="text"
                value={formData.price.wh.opdSubsidized}
                onChange={(e) =>
                  handlePriceChange("wh.opdSubsidized", e.target.value)
                }
                placeholder="Vnd 0"
                className="form-section-input-2"
              />
            </div>
          </div>
        </div>

        {/* Price Non-Working Hour Section */}
        <div className="whp-container">
          <div className="title-hr2">
            <p>{t("priceNonWorkingHour") || "Set price for non-working hours"}</p>
            <hr />
          </div>
          <p className="clickable-text" onClick={handleSameAsWorkingHours}>
            {t("sameAsWorkingHour") || "Same as working hours"}
          </p>
          <div className="service-type-main2">
            <div className="service-input-box">
              <span>
                {t("opdInterInsured")} <label>*</label>
              </span>
              <input
                type="text"
                value={formData.price.nwh.opdInternationalPrice}
                onChange={(e) =>
                  handlePriceChange("nwh.opdInternationalPrice", e.target.value)
                }
                placeholder="Vnd 0"
                className="form-section-input-2"
              />
            </div>
            <div className="service-input-box">
              <span>
                {t("opdLocalInsured")} <label>*</label>
              </span>
              <input
                type="text"
                value={formData.price.nwh.opdLocalPrice}
                onChange={(e) =>
                  handlePriceChange("nwh.opdLocalPrice", e.target.value)
                }
                placeholder="Vnd 0"
                className="form-section-input-2"
              />
            </div>
            <div className="service-input-box">
              <span>
                {t("opdSubsidized")} <label>*</label>
              </span>
              <input
                type="text"
                value={formData.price.nwh.opdSubsidized}
                onChange={(e) =>
                  handlePriceChange("nwh.opdSubsidized", e.target.value)
                }
                placeholder="Vnd 0"
                className="form-section-input-2"
              />
            </div>
          </div>
        </div>

        {/* PDF Upload Section */}
        <div className="pdf-upload-container">
          <span className="pdf-heading">
            {t("uploadChecklistPdf")} <label>*</label>
          </span>
          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            id="pdfUpload"
            hidden
          />
          <div className="service-pdf-wrapper">
            {!pdfUrl ? (
              <label
                htmlFor="pdfUpload"
                className="service-pdf-card service-pdf-preview"
              >
                <img
                  src={Icons.PdfRedIcon}
                  className="service-pdf-icon"
                  alt="Upload PDF"
                />
                <span className="service-pdf-name">
                  {uploading ? t("uploading") : t("uploadPdf") || "Upload PDF"}
                </span>
              </label>
            ) : (
              <div className="service-pdf-card service-pdf-preview">
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                  <img
                    src={Icons.PdfRedIcon}
                    className="service-pdf-icon"
                    alt="Checklist PDF"
                  />
                  <span className="service-pdf-name">
                    {pdfFile?.name || "Checklist.pdf"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleRemovePdf}
                  className="service-pdf-delete"
                >
                  {t("remove") || "Remove"}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Form Footer */}
        <div className="form-section-footer">
          <button type="button" className="form-section-cancel" onClick={handleCancel}>
            {t("cancel")}
          </button>
          <button type="button" className="form-section-create" onClick={handleCreate}>
            {t("createEntry") || "Create Entry"}
          </button>
        </div>
      </div>

      {/* Create Service Group Modal - Multilingual */}
      {isServiceGroupModalOpen && (
        <div className="mr-modal">
          <div className="modal-content" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", backgroundColor: "#fff", padding: "20px", borderRadius: "8px", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)", width: "400px", zIndex: 1000 }}>
            <h2>{t("createServiceGroup")}</h2>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "18px" }}>
                {t("englishName")} <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                value={newServiceTitle.en}
                onChange={(e) => setNewServiceTitle((prev) => ({ ...prev, en: e.target.value }))}
                className="modal-input"
                placeholder={t("enterServiceNameEn")}
                style={{ width: "100%", padding: "8px", marginBottom: "1rem" }}
              />
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "18px" }}>
                {t("vietnameseName")} <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                value={newServiceTitle.vi}
                onChange={(e) => setNewServiceTitle((prev) => ({ ...prev, vi: e.target.value }))}
                className="modal-input"
                placeholder={t("enterServiceNameVi")}
                style={{ width: "100%", padding: "8px", marginBottom: "1rem" }}
              />
            </div>
            <div className="modal-actions">
              <button
                onClick={() => {
                  setIsServiceGroupModalOpen(false);
                  setNewServiceTitle({ en: "", vi: "" });
                }}
                className="modal-btn modal-cancel"
              >
                {t("cancel")}
              </button>
              <button onClick={handleCreateServiceGroup} className="modal-btn modal-create">
                {t("create")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Sub-Service Group Modal - Multilingual */}
      {isSubServiceGroupModalOpen && (
        <div className="mr-modal">
          <div className="modal-content" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", backgroundColor: "#fff", padding: "20px", borderRadius: "8px", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)", width: "400px", zIndex: 1000 }}>
            <h2>{t("createSubServiceGroup")}</h2>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "18px" }}>
                {t("englishName")} <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                value={newSubServiceTitle.en}
                onChange={(e) => setNewSubServiceTitle((prev) => ({ ...prev, en: e.target.value }))}
                className="modal-input"
                placeholder={t("enterSubServiceNameEn")}
                style={{ width: "100%", padding: "8px", marginBottom: "1rem" }}
              />
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "18px" }}>
                {t("vietnameseName")} <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                value={newSubServiceTitle.vi}
                onChange={(e) => setNewSubServiceTitle((prev) => ({ ...prev, vi: e.target.value }))}
                className="modal-input"
                placeholder={t("enterSubServiceNameVi")}
                style={{ width: "100%", padding: "8px", marginBottom: "1rem" }}
              />
            </div>
            <div className="modal-actions">
              <button
                onClick={() => {
                  setIsSubServiceGroupModalOpen(false);
                  setNewSubServiceTitle({ en: "", vi: "" });
                }}
                className="modal-btn modal-cancel"
              >
                {t("cancel")}
              </button>
              <button onClick={handleCreateSubServiceGroup} className="modal-btn modal-create">
                {t("create")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateServiceMain;
